<app-auth-shell>
  @if (validatingToken) {
    <div class="checking" role="status">
      <span class="spinner" aria-hidden="true"></span>
      <div class="auth_heading">
        <h1>Checking your reset link</h1>
        <p>This takes a moment.</p>
      </div>
    </div>
  } @else if (passwordUpdatedSuccessfully) {
    <div class="auth_state">
      <app-status-illustration kind="password-updated"></app-status-illustration>
      <div class="auth_heading">
        <h1>Password updated</h1>
        <p>Sign in with your new password.</p>
      </div>
      <button type="button" class="primary_button auth_submit" (click)="goToLogin()">Sign in</button>
    </div>
  } @else if (!validToken) {
    <div class="auth_state">
      <app-status-illustration kind="link-expired"></app-status-illustration>
      <div class="auth_heading">
        <h1>This reset link has expired</h1>
        <p>Reset links work once and only for a limited time. Request a new one to continue.</p>
      </div>
      <button type="button" class="primary_button auth_submit" (click)="goToForgotPassword()">Request a new link</button>
      <a class="back_link" routerLink="/login"><svg viewBox="0 0 16 16" aria-hidden="true"><path d="M10 3.5 5.5 8l4.5 4.5" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg> Back to sign in</a>
    </div>
  } @else {
    <div class="auth_heading">
      <h1>Set a new password</h1>
      <p>Choose a new password for your Smart TOS account.</p>
    </div>

    <form [formGroup]="resetPasswordForm" (ngSubmit)="requestResetPassword()">
      @if (otpRequired) {
        <div class="input-with-label">
          <label for="otp" class="required_field_label">Verification code</label>
          @if (obfuscatedMobileNumber) {
            <span class="field_hint">Sent to {{ obfuscatedMobileNumber }}</span>
          }
          <app-otp-input
            id="otp"
            [ngClass]="{ form_error: resetPasswordForm.get('otp')?.invalid && resetPasswordForm.get('otp')?.touched }"
            (otpChange)="onOtpChange($event)"
          ></app-otp-input>
          <div class="validation_error" *ngIf="resetPasswordForm.get('otp')?.invalid && resetPasswordForm.get('otp')?.touched">
            <small *ngIf="resetPasswordForm.get('otp')?.errors?.['required']">Verification code is required</small>
          </div>
          <div class="phone_login_options">
            <span class="countdown">
              @if (!resendAllowed) { Resend code in {{ formatTime(countdown) }} }
            </span>
            <a [ngClass]="{ disabled: !resendAllowed }" (click)="resendOtp()">Resend code</a>
          </div>
        </div>
      }

      <div class="input_with_label">
        <label for="newPassword" class="required_field_label">New password</label>
        <div class="password_field">
          <input
            id="newPassword"
            name="newPassword"
            formControlName="newPassword"
            (input)="validatePassword()"
            [type]="showNewPassword ? 'text' : 'password'"
            placeholder="Enter a new password"
            autocomplete="new-password"
            required
            aria-describedby="password-checks"
            [ngClass]="{ form_error: resetPasswordForm.get('newPassword')?.touched && passwordInvalid }"
          />
          <button type="button" class="toggle-password-btn" [attr.aria-label]="showNewPassword ? 'Hide password' : 'Show password'"
                  (click)="showNewPassword = !showNewPassword">
            <app-svg-icon [path]="showNewPassword ? 'assets/icons/view_icon.svg' : 'assets/icons/eye-off.svg'" />
          </button>
        </div>
        <ul class="checks" id="password-checks" aria-label="Password requirements">
          @for (check of passwordChecks; track check.label) {
            <li [class.met]="check.met">
              <svg viewBox="0 0 16 16" aria-hidden="true">
                @if (check.met) {
                  <path d="M3.5 8.5l3 3 6-7" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                } @else {
                  <circle cx="8" cy="8" r="2.5" fill="currentColor"/>
                }
              </svg>
              {{ check.label }}
              <span class="visually_hidden">{{ check.met ? '(done)' : '(missing)' }}</span>
            </li>
          }
        </ul>
        <div class="validation_error" *ngIf="resetPasswordForm.get('newPassword')?.touched && passwordInvalid">
          <small *ngIf="resetPasswordForm.get('newPassword')?.errors?.['required']; else unmet">New password is required</small>
          <ng-template #unmet><small>Meet every requirement above</small></ng-template>
        </div>
      </div>

      <div class="input_with_label">
        <label for="confirmPassword" class="required_field_label">Confirm new password</label>
        <div class="password_field">
          <input
            id="confirmPassword"
            name="confirmPassword"
            formControlName="confirmPassword"
            [type]="showConfirmPassword ? 'text' : 'password'"
            placeholder="Enter the new password again"
            autocomplete="new-password"
            required
            [ngClass]="{
              form_error:
                resetPasswordForm.get('confirmPassword')?.touched &&
                (resetPasswordForm.get('confirmPassword')?.errors || resetPasswordForm.hasError('passwordMismatch'))
            }"
          />
          <button type="button" class="toggle-password-btn" [attr.aria-label]="showConfirmPassword ? 'Hide password' : 'Show password'"
                  (click)="showConfirmPassword = !showConfirmPassword">
            <app-svg-icon [path]="showConfirmPassword ? 'assets/icons/view_icon.svg' : 'assets/icons/eye-off.svg'" />
          </button>
        </div>
        <div class="validation_error"
             *ngIf="resetPasswordForm.get('confirmPassword')?.touched &&
                    (resetPasswordForm.get('confirmPassword')?.errors || resetPasswordForm.hasError('passwordMismatch'))">
          <small *ngIf="resetPasswordForm.get('confirmPassword')?.errors?.['required']; else mismatch">Confirm your new password</small>
          <ng-template #mismatch><small>Passwords don't match</small></ng-template>
        </div>
      </div>

      <button type="submit" class="primary_button auth_submit">Update password</button>
    </form>

    <a class="back_link" routerLink="/login"><svg viewBox="0 0 16 16" aria-hidden="true"><path d="M10 3.5 5.5 8l4.5 4.5" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg> Back to sign in</a>
  }
</app-auth-shell>
